'use client'

/**
 * The search input (§30).
 *
 * A plain form with `method="get"` that navigates to /search?q=… — which is the
 * important part. A search engine's results must be linkable, shareable and
 * survivable by the back button, and that comes free from a real URL rather
 * than from client state.
 *
 * ── Saying that Enter was heard (2026-08-12) ────────────────────────────────
 *
 * `router.push` used to be called bare, and a bare push is silent. An App Router
 * navigation is not finished until the server has rendered the new page and the
 * payload has crossed the wire; until then the browser keeps showing the page
 * you were on. Measured on loopback, with a warm backend and no network at all,
 * typing a second keyword on the results page left the FIRST keyword's results
 * on screen for 622ms with nothing moving anywhere. Over the internet that is
 * seconds. It does not read as "loading", it reads as a broken Enter key — and
 * the natural response is to press it again.
 *
 * So the push happens inside a transition, whose `pending` flag is true for
 * exactly that window: from the keypress until the new page is on screen. The
 * magnifier becomes a spinner for the duration, and the results area streams in
 * a skeleton behind it (`app/search/page.tsx`).
 *
 * Both halves of the swap are on a 120ms animation delay, so a navigation that
 * lands sooner than that — a cached query, a page already prefetched — shows
 * nothing at all rather than a flicker. Feedback is for waiting; there is no
 * point announcing a wait that did not happen.
 */
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState, useTransition } from 'react'

function SearchIcon() {
  return (
    <svg className="icon sb-glass" width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
      <path d="m20 20-3.6-3.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

/**
 * The same 19×19 box as the magnifier, stacked in the same grid cell.
 *
 * Same size and same cell so the swap moves nothing: the input's left edge is
 * where it was, and a caret sitting in the field does not shift sideways while
 * the search runs.
 */
function SearchSpinner() {
  return (
    <svg className="icon sb-spin" width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="2" opacity="0.22" />
      <path d="M12 3.5a8.5 8.5 0 0 1 8.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

export function SearchBox({
  initialQuery = '',
  autoFocus = false,
  type,
}: {
  initialQuery?: string
  autoFocus?: boolean
  /** Carried through so refining a query does not silently drop the active tab. */
  type?: string
}) {
  const router = useRouter()
  const [value, setValue] = useState(initialQuery)
  const inputRef = useRef<HTMLInputElement>(null)
  const [pending, startTransition] = useTransition()

  // Keep the box in step with the URL when navigating back, forward, or between
  // tabs — otherwise it keeps showing whatever was last typed.
  useEffect(() => {
    setValue(initialQuery)
  }, [initialQuery])

  function submit(event: React.FormEvent) {
    event.preventDefault()
    const q = value.trim()
    if (!q) return
    const params = new URLSearchParams({ q })
    if (type && type !== 'all') params.set('type', type)
    // Inside the transition, so `pending` covers the whole navigation rather
    // than just the call. Not guarded against a second Enter: React transitions
    // are interruptible, so someone who corrects their spelling mid-search gets
    // the corrected search instead of being told to wait for the wrong one.
    startTransition(() => {
      router.push(`/search?${params.toString()}`)
    })
  }

  return (
    <form
      className="searchbox"
      onSubmit={submit}
      role="search"
      // Marks the whole control as working, which is what a screen reader's
      // "busy" announcement is for. The spinner beside it is the same fact
      // stated visually.
      aria-busy={pending || undefined}
    >
      <span className={`sb-icon ${pending ? 'is-searching' : ''}`}>
        <SearchIcon />
        <SearchSpinner />
      </span>
      <input
        ref={inputRef}
        name="q"
        value={value}
        autoFocus={autoFocus}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Describe what you need…"
        aria-label="Describe what you need"
      />
      {/*
        Announced, not just drawn.

        A spinner is invisible to a screen reader, and this is the one moment
        where the page has stopped responding to a keypress on purpose. The
        region is always mounted and only its text changes, because a live
        region that appears at the same instant as its text is frequently not
        announced at all.
      */}
      <span className="sr-only" role="status" aria-live="polite">
        {pending ? `Searching for ${value.trim()}…` : ''}
      </span>
      {value && (
        <button
          type="button"
          className="clear"
          aria-label="Clear search"
          onClick={() => {
            setValue('')
            inputRef.current?.focus()
          }}
        >
          ×
        </button>
      )}
    </form>
  )
}

/**
 * Example queries on the landing page — §3's own list, so they always work.
 *
 * Same silent-push problem as the box above, and worse here: a button that does
 * not move when pressed is the classic "click it again" trap. The one that was
 * pressed goes busy — that one and not the whole row, so the answer to "which
 * did I click?" is on screen while the search runs.
 */
export function ExampleQueries({ queries }: { queries: string[] }) {
  const router = useRouter()
  const [pending, startTransition] = useTransition()
  const [chosen, setChosen] = useState<string | null>(null)

  return (
    <div className="examples">
      {queries.map((q) => (
        <button
          key={q}
          className={`example ${pending && chosen === q ? 'is-searching' : ''}`}
          type="button"
          aria-busy={pending && chosen === q ? true : undefined}
          onClick={() => {
            setChosen(q)
            startTransition(() => {
              router.push(`/search?q=${encodeURIComponent(q)}`)
            })
          }}
        >
          {q}
        </button>
      ))}
    </div>
  )
}
